import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatCurrency = (value) => {
  const num = parseFloat(value);
  return "$" + (isNaN(num) ? "0.00" : num.toFixed(2));
};

const renderAddressLines = (addressInfo) => {
  if (!addressInfo) return [{ label: null, value: "N/A" }];
  const lines = [];
  const name = `${addressInfo?.fname || ""} ${addressInfo?.lname || ""}`.trim();
  if (name) lines.push({ label: null, value: name });
  if (addressInfo?.address_1) lines.push({ label: null, value: addressInfo.address_1 });
  if (addressInfo?.address_2) lines.push({ label: null, value: addressInfo.address_2 });
  const cityLine = [addressInfo?.city, addressInfo?.postal].filter(Boolean).join(", ");
  if (cityLine) lines.push({ label: null, value: cityLine });
  const stateLine = [addressInfo?.state, addressInfo?.country].filter(Boolean).join(", ");
  if (stateLine) lines.push({ label: null, value: stateLine });
  if (addressInfo?.phone) lines.push({ label: "Phone:", value: addressInfo.phone });
  if (addressInfo?.email) lines.push({ label: "Email:", value: addressInfo.email });
  return lines.length ? lines : [{ label: null, value: "N/A" }];
};

const getTransactionSummary = (txnBody) => {
  if (!txnBody || typeof txnBody !== "object") return [];
  const candidates = [
    ["Transaction ID", txnBody.id || txnBody.transaction_id],
    ["Status", txnBody.status],
    ["Payment Method", txnBody.payment_method || txnBody.payment_method_type],
    ["Amount", txnBody.amount ? formatCurrency(txnBody.amount) : undefined],
    ["Currency", txnBody.currency],
  ];
  return candidates.filter(([, value]) => value !== undefined && value !== null && value !== "");
};

export const generateOrderPdf = (order) => {
  if (!order) return;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 40;
  let cursorY = 50;

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Order Confirmation", marginX, cursorY);

  doc.setFontSize(10);
  cursorY += 25;

  doc.setFont("helvetica", "bold");
  doc.text("Order Number:", marginX, cursorY);
  doc.setFont("helvetica", "normal");
  doc.text(
    ` BP${order.order_id || order.id || "N/A"}`,
    marginX + doc.getTextWidth("Order Number:"),
    cursorY
  );
  cursorY += 16;

  doc.setFont("helvetica", "bold");
  doc.text("Date:", marginX, cursorY);
  doc.setFont("helvetica", "normal");
  doc.text(` ${order.created || "N/A"}`, marginX + doc.getTextWidth("Date:"), cursorY);

  cursorY += 30;

  const billLines = renderAddressLines(order.billing_address_info);
  const shipLines = renderAddressLines(order.shipping_address_info);
  const colWidth = (pageWidth - marginX * 2 - 20) / 2;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Bill To", marginX, cursorY);
  doc.text("Ship To", marginX + colWidth + 20, cursorY);

  doc.setFontSize(9.5);
  const lineHeight = 13;

  const renderAddressColumn = (lines, x, startY) => {
    let y = startY;
    lines.forEach(({ label, value }) => {
      if (label) {
        doc.setFont("helvetica", "bold");
        doc.text(label, x, y);
        const labelWidth = doc.getTextWidth(`${label} `);
        doc.setFont("helvetica", "normal");
        const wrapped = doc.splitTextToSize(value, colWidth - labelWidth);
        doc.text(wrapped, x + labelWidth, y);
        y += wrapped.length * lineHeight;
      } else {
        doc.setFont("helvetica", "normal");
        const wrapped = doc.splitTextToSize(value, colWidth);
        doc.text(wrapped, x, y);
        y += wrapped.length * lineHeight;
      }
    });
    return y;
  };

  const billY = renderAddressColumn(billLines, marginX, cursorY + 15);
  const shipY = renderAddressColumn(shipLines, marginX + colWidth + 20, cursorY + 15);

  cursorY = Math.max(billY, shipY) + 15;

  const items = (order.items || []).map((item) => [
    item.product_name || "",
    item.quantity ?? "",
    formatCurrency(item.unit_price),
    formatCurrency((parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0)),
  ]);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    head: [["Product Name", "Quantity", "Unit Price", "Total"]],
    body: items.length ? items : [["No items", "", "", ""]],
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: { fillColor: [124, 19, 134] },
  });

  cursorY = doc.lastAutoTable.finalY + 20;

  const summaryRows = [
    ["Shipping Method", order.shipping_method || "N/A"],
    ["Product Price", formatCurrency(order.product_total_price)],
    ["Shipping Fee", formatCurrency(order.Shipping_fee)],
  ];
  if (order.discounted_amount && parseFloat(order.discounted_amount) > 0) {
    summaryRows.push(["Discount", `- ${formatCurrency(order.discounted_amount)}`]);
  }
  summaryRows.push(["Total Amount", formatCurrency(order.total_amount)]);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: pageWidth - marginX - 220, right: marginX },
    tableWidth: 220,
    body: summaryRows,
    styles: { fontSize: 9.5, cellPadding: 5 },
    theme: "plain",
    columnStyles: {
      0: { fontStyle: "normal" },
      1: { fontStyle: "bold", halign: "right" },
    },
    didParseCell: (data) => {
      if (data.row.raw[0] === "Total Amount") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fontSize = 10.5;
      }
    },
  });

  cursorY = doc.lastAutoTable.finalY + 25;

  let txnBody = null;
  try {
    txnBody = typeof order.txn_body === "string" ? JSON.parse(order.txn_body) : order.txn_body;
  } catch (e) {
    txnBody = null;
  }

  const txnSummary = getTransactionSummary(txnBody);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Payment Transaction", marginX, cursorY);
  cursorY += 15;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);

  if (order.txn_id) {
    doc.text(`Transaction Order ID: ${order.txn_id}`, marginX, cursorY);
    cursorY += 13;
  }
  if (txnSummary.length) {
    txnSummary.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, marginX, cursorY);
      cursorY += 13;
    });
  } else if (!order.txn_id) {
    doc.text("No transaction details available", marginX, cursorY);
    cursorY += 13;
  }

  cursorY += 15;

  const checkPageBreak = (linesNeeded = 1) => {
    const lineHeight = 13;
    if (cursorY + linesNeeded * lineHeight > doc.internal.pageSize.getHeight() - 40) {
      doc.addPage();
      cursorY = 50;
    }
  };

  const writeParagraph = (title, lines) => {
    checkPageBreak(lines.length + 2);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.text(title, marginX, cursorY);
    cursorY += 15;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    lines.forEach((line) => {
      const wrapped = doc.splitTextToSize(line, pageWidth - marginX * 2);
      wrapped.forEach((wrappedLine) => {
        checkPageBreak(1);
        doc.text(wrappedLine, marginX, cursorY);
        cursorY += 13;
      });
    });
    cursorY += 10;
  };

  writeParagraph("Contact Information:", [
    "If you have any questions or concerns regarding your order, please contact our customer support team at instruments@betterphysiology.com, or call us at +1 505.820.3376.",
  ]);

  writeParagraph("Tech Support:", [
    "For initial Tech support regarding software download problems and/or hardware issues, email tech@betterphysiology.com for immediate live Zoom support.",
  ]);

  checkPageBreak(2);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9.5);
  doc.text(
    "Thank you for choosing Better Physiology Ltd. We appreciate your business!",
    marginX,
    cursorY
  );

  doc.save(`Order_Confirmation_${order.order_id || order.id || ""}.pdf`);
};

export default generateOrderPdf;
